'use strict'

const {setValue, CS_CONFIG_SET} = require('./event');
const {getCountry, getEnvCode, getClientId} = require('./runConfigService');
const {getShoppingContext, getShoppingContextName} = require('./shoppingContext');
const assert = require('assert');
const axios = require('axios');
const _ = require('lodash');    // used for deep merges
const { v1: uuidV1 } = require('uuid');
const Cache = require('./cache');


const STORAGE_KEY = 'marketConfigurationOverrides';
const DEFAULT_CONTEXT = 'noCntxt';

const baseUrl = {
    dev: 'https://devapi.cloud.nuskin.com',
    test: 'https://testapi.cloud.nuskin.com',
    stage: 'https://stageapi.cloud.nuskin.com',
    prod: 'https://api.cloud.nuskin.com'
};

const cacheConfig = {
    methods: ['post', 'get'],
    ttl: 2 * 60 * 1000// two minutes
}

const axiosCreate = axios.create || axios.default.create
const api  = axiosCreate()


let configurationOverrides = {noCntxt: {}};
let legacyOverrides = {}; // legacy overrides stored in shoppingContext in localStorage

// This is a queue to store promises as calls to getConfiguration are made. There will be
// a promise for each configMap requested. These promises are modified to store the configMap
// name as well as a pid (promise Id) to identity which configMap it goes with.
let requestQueue = [];

// Setup default getItem, setItem so the sdk will still function in a lambda
const storage = {
    getItem: function (key) {
        return this[key]
    },
    setItem: function (key, value) {
        this[key] = value;
    }
};
if (typeof sessionStorage !== "undefined") {
    storage.setItem = (key, value) => sessionStorage.setItem(key, value);
    storage.getItem = (key) => sessionStorage.getItem(key);
}

/**
 * Builds graphql query
 * @param {Object} queryFields
 * @return {string} graphql query
 * @private
 */
const _getQuery = (queryFields) => {
    let queryBody = '';
    Object.keys(queryFields).forEach((configMap) => {
        queryBody += `${configMap} { ${queryFields[configMap].toString()} }`
    });

    return `query getConfigMaps($configMaps: [String!]!, $country: String!, $shoppingContext: String) {
        configMaps(configMaps: $configMaps, country: $country, shoppingContext: $shoppingContext) {
            ${queryBody}
        }
    }`
};

/**
 * Syncs configuration overrides from sessionstorage with in memory configuration overrides
 * @private
 */
const _syncConfigurationOverrides = () => {
    const confOverrides = storage.getItem(STORAGE_KEY);
    if (confOverrides) {
        configurationOverrides = _.merge(configurationOverrides, JSON.parse(confOverrides));
    }
};

/**
 * Initialize the configuration-sdk with functions for getting and setting items from storage
 * If you are using the sdk in a browser you do not need to run init
 * @deprecated
 */
const init = () => {
    // Removed unnecessary functionality since this is deprecated.
    console.log('This function is deprecated you no longer need to call init');
};

/**
 *  Loads the legacy overrides from shoppingContext in localStorage
 */
const getLegacyOverrides = () => {
    const context = getShoppingContext();
    if (context) {
        if (typeof context.overrides === 'object') {
            legacyOverrides = Object.assign({}, context.overrides);
        }
    }
};

/**
 * Gets the option values for querying contentStack
 * @param {Object} options
 * @param {string} options.country
 * @param {string} options.clientId
 * @param {string} options.shoppingContext
 * @returns {Object}
 */
const getOptions = (options) => {
    const country = options.country || getCountry();
    const environment = options.environment || getEnvCode();
    const clientId = options.clientId || getClientId(options);
    const contextName = options.shoppingContext && options.shoppingContext != DEFAULT_CONTEXT
        ? options.shoppingContext
        : getShoppingContextName();
    const shoppingContext = contextName || DEFAULT_CONTEXT;

    return {country, environment, clientId, shoppingContext};
};

/**
 * Function that allows for overriding of configuration properties
 * @param {Object} options
 * @param {string} options.configMap
 * @param {string} options.configMapProperty
 * @param {string} options.country
 * @param {string} options.value
 */
const overrideConfigurationProperty = (
    {configMap, configMapProperty, country: _country, value, shoppingContext = DEFAULT_CONTEXT}
) => {
    const {shoppingContext: shoppingCntxt} = getOptions({country: _country, shoppingContext});
    const shopCntxt = {
        [configMap]: {
            [configMapProperty]: value
        }
    };
    configurationOverrides[shoppingCntxt] = _.merge(configurationOverrides[shoppingCntxt], shopCntxt);
    storage.setItem(STORAGE_KEY, JSON.stringify(configurationOverrides));
};

/**
 * Function that allows for overriding of configuration
 * @param {Object} config
 * @param _country
 */
const overrideConfiguration = (config, _country) => {
    const {shoppingContext} = getOptions({country: _country});
    configurationOverrides[shoppingContext] = _.merge(configurationOverrides[shoppingContext], config);
    storage.setItem(STORAGE_KEY, JSON.stringify(configurationOverrides));
}

const getConfigmapOverride = (configName, shoppingContext) => {
    return ((configurationOverrides[shoppingContext]  || {}) || {})[configName] || {};
}

/**
 * Clears configuration overrides
 */
const clearConfigurationOverrides = (shoppingContext = DEFAULT_CONTEXT) => {
    const {shoppingContext: shoppingCntxt} = getOptions({shoppingContext});
    configurationOverrides[shoppingCntxt] = {};
    storage.setItem(STORAGE_KEY, JSON.stringify(configurationOverrides));
};

/**
 * Gets configuration from content stack with graphql query through the contentstack configuration lambda
 * @param {Object} queryFields
 * @param {string} options
 * @return {Promise<any>} configuration
 */
const getPartialConfig = async (queryFields, options = {}) => {
    assert(queryFields, 'configMapNames is required');
    const {country, environment, clientId, shoppingContext} = getOptions(options);

    _syncConfigurationOverrides();
    const body = {
        query: _getQuery(queryFields),
        variables: {
            configMaps: Object.keys(queryFields),
            country,
            ...(shoppingContext === DEFAULT_CONTEXT ? {} : {shoppingContext})
        }
    }
    const response = await api({
        url: `${baseUrl[environment]}/contentstack-configuration/v1`,
        method: 'post',
        data: JSON.stringify(body),
        headers: {
            "content-type": "application/json",
            "client_id": clientId
        },
        cache: cacheConfig
    });

    // Apply Overrides
    const retVal = _.merge(
        ((response.data || {}).data || {}).configMaps,
        ((configurationOverrides[shoppingContext] || {})[country] || {})
    );

    // apply legacy context overrides
    getLegacyOverrides();
    Object.keys(retVal).forEach((mapKey) => {
        retVal[mapKey] = Object.assign(retVal[mapKey], legacyOverrides);
    });

    return retVal;
};

/**
 * defer creates a promise and then adds the resovle/reject functions to it as well
 * as the configMap name and a pid to identify it with. The pid is needed because the
 * queue (requestQueue) it is being added to may contain more than one promise for
 * a single configMap.
 *
 * @param {string} mapName
 * @return {Promise}
 */
const defer = (mapName) => {
    let res, rej;

    const promise = new Promise((resolve, reject) => {
        res = resolve;
        rej = reject;
    });

    promise.pid = uuidV1();
    promise.mapName = mapName;
    promise.resolve = res;
    promise.reject = rej;

    return promise;
}

/**
 * This takes a list of configMap names and checks the requestQueue to see if
 * there are any promises already there for any of the names passed in. If
 * there are then this will wait until those promises have been resolved
 * before returning.
 *
 * This also creates promises for each of the configMap names passed in and
 * adds them to the requestQueue. This is done to block subsequent calls to
 * get configMaps.
 *
 * @param {string[]} configMapNames
 * @return {Promise[]}
 */
const waitForLoadingConfigMaps = async (configMapNames = []) => {
    const waitPromises = [];
    const newPromises = [];

    configMapNames.forEach((mapName) => {
        requestQueue.forEach((rq) => {
            if (rq.mapName === mapName) {
                waitPromises.push(rq);
            }
        });
        newPromises.push(defer(mapName));
    });

    requestQueue = requestQueue.concat(newPromises);
    if (waitPromises.length > 0) {
        await Promise.all(waitPromises);
    }

    return newPromises;
};

/**
 * This takes an array of promises that have been created by the defer
 * function and resolves them. It also removes these promises from the
 * requestQueue
 *
 * @param {promise[]} promises
 */
const resolveRequestPromises = (promises = []) => {
    promises.forEach((p) => p.resolve());
    requestQueue = requestQueue.filter((rq) => !promises.some((p) => p.pid === rq.pid))
};

const getConfigMap = (country, configMapName, shoppingContext, environment, clientId) => {
    const url = `${baseUrl[environment]}/contentstack-configuration/v1/${configMapName}/${country}`;
    const fullUrl = shoppingContext !== DEFAULT_CONTEXT ? `${url}/${shoppingContext}` : url;
    return api({
        url: fullUrl,
        method: 'get',
        headers: {
            "client_id": clientId
        },
        cache: cacheConfig
    });
};

const getConfigMaps = (country, configMaps, shoppingContext, environment, clientId) => {
    const url = `${baseUrl[environment]}/contentstack-configuration/v1/maps`;
    const body = {market: country, configMaps: configMaps};
    if (shoppingContext !== DEFAULT_CONTEXT) body.shoppingContext = shoppingContext;
    return api({
        url: url,
        method: 'put',
        data: JSON.stringify(body),
        headers: {
            'content-type': 'application/json',
            "client_id": clientId
        },
        cache: cacheConfig
    });
};

/**
 * Gets the full configuration for given configMaps
 * @param {string[]} configMapNames - Ex: ['Ordering']
 * @param {Object} options
 * @param {string} options.country - Ex: US - optional for client
 * @param {string} options.shoppingContext - Ex: storefront - optional for client
 * @param {string} options.clientId - Ex: 893484490289384 - optional for client
 * @return {Promise<any>} Configuration
 */
const getConfiguration = async (configMapNames, options = {}) => {
    assert(Array.isArray(configMapNames), 'configMapNames is required');

    // Check to see if there are any current requests happening for the same
    // configMaps for this request. This call also adds promises to the
    // requestQueue to possibly block a subsequent call(s) to getConfiguration.
    const configPromises = await waitForLoadingConfigMaps(configMapNames);

    const {country, environment, clientId, shoppingContext} = getOptions(options);
    Cache.checkCountry(country);

    _syncConfigurationOverrides();

    const promises = [];
    const cachedConfigs = {};
    const configsToGet = [];
    configMapNames.map((configMapName) => {
        try {
            cachedConfigs[configMapName] = getCachedConfiguration(configMapName, false);
        } catch (err) {
            configsToGet.push(configMapName);
        }
    });
    if (configsToGet.length === 1) {
        promises.push(getConfigMap(country, configsToGet[0], shoppingContext, environment, clientId));
    } else if (configsToGet.length > 1) {
        promises.push(getConfigMaps(country, configsToGet, shoppingContext, environment, clientId));
    }

    let results;
    try {
        results = await Promise.all(promises);
    } catch (err) {
        // see comment for the call to resolveRequestPromises at end of this function below.
        resolveRequestPromises(configPromises);
        throw err;
    }

    let retVal = cachedConfigs;
    results.forEach((result) => {
        retVal = Object.assign(retVal, result.data);
    });

    Cache.updateCache(retVal);

    // apply overrides to what's being returned.
    retVal = _.merge(retVal, ((configurationOverrides[shoppingContext]  || {})[country] || {}));

    // apply legacy context overrides
    getLegacyOverrides();
    Object.keys(retVal).forEach((mapKey) => {
        retVal[mapKey] = Object.assign(retVal[mapKey], legacyOverrides);
    });

    // This call will resolve the promises created for the list of configMaps
    // names passed into this call. It also removes them from the requestQueue.
    resolveRequestPromises(configPromises);

    setValue(CS_CONFIG_SET, [Cache.keys()]);
    return retVal;
};



/**
 * Loads config from cache previously loaded from getConfiguration
 *
 * @param {string[]} configNames
 */
const getCachedConfigurations = (configNames = []) => {
    const retVal = {};
    const {shoppingContext} = getOptions({});
    getLegacyOverrides();

    if (!Array.isArray(configNames) || configNames.some((name) => typeof name != 'string')) {
        throw new Error('getCachedConfigurations requires an array of strings');
    }
    Cache.checkConfigCache(configNames);
    configNames.forEach((configName) => {
        const overrides = getConfigmapOverride(configName, shoppingContext);
        retVal[configName] = _.merge({}, Cache.get(configName), overrides, legacyOverrides);
    });
    retVal.overrides = legacyOverrides;

    return retVal
};

/**
 * Loads config from cache previously loaded from getConfiguration
 *
 * @param {string} configName
 */
const getCachedConfiguration = (configName, applyOverrides = true) => {
    if (typeof configName != 'string') {
        throw new Error('getCachedConfiguration configName requires a string');
    }
    const {shoppingContext} = getOptions({});
    Cache.checkConfigCache([configName]);
    getLegacyOverrides();
    const overrides = applyOverrides ? getConfigmapOverride(configName, shoppingContext) : {};

    return _.merge({}, Cache.get(configName), overrides, legacyOverrides);
};

/**
 * Retrieves a field from the cache. Fields are required to be
 * unique across config maps.
 *
 * @param {string} configField
 */
const getCachedConfigField = (configField) => {
    const {shoppingContext} = getOptions({});
    let matchedConfig = null;
    getLegacyOverrides();

    Cache.keys()
        .find(configName => {
            const configOverride = getConfigmapOverride(configName, shoppingContext);
            const config = _.merge({}, Cache.get(configName), configOverride, legacyOverrides);
            const found = Object.keys(config).some(k => k === configField);
            matchedConfig = found ? config : null;
            return found;
        });

    if (!matchedConfig) {
        throw new Error(`${configField} not found in any cache. Was the configMap previously loaded?`);
    }
    return matchedConfig[configField];
};

module.exports = {
    getConfiguration,
    getCachedConfigurations,
    getCachedConfiguration,
    getCachedConfigField,
    getPartialConfig,
    overrideConfigurationProperty,
    overrideConfiguration,
    clearConfigurationOverrides,
    clearConfigCache: Cache.clear,
    init
};
